import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useOrder } from '../context/OrderContext';
import Swal from 'sweetalert2';

const DraftOrdersList = () => {
    const [draftOrders, setDraftOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();
    const { dispatch } = useOrder();

    useEffect(() => {
        const fetchDraftOrders = async () => {
            if (!user) return;

            try {
                setLoading(true);
                // Determine user type based on role
                // Agent roles: 'agent', 'head_agent'
                // Client role: 'client' or anything else
                const isAgent = user.role === 'agent' || user.role === 'head_agent';
                const userType = isAgent ? 'agent' : 'client';
                // For agents use id (which is Salesperson), for clients use CustSupp or id
                const userId = isAgent ? user.id : (user.CustSupp || user.client_data?.CustSupp || user.id);

                // Get draft orders for the current user
                const response = await api.getDraftOrders(userId, userType);
                if (response.status === 'success') {
                    setDraftOrders(response.draft_orders);
                } else {
                    setError('Errore nel caricamento delle bozze');
                }
            } catch (err) {
                console.error('Error fetching draft orders:', err);
                setError('Errore nel caricamento delle bozze');
            } finally {
                setLoading(false);
            }
        };

        fetchDraftOrders();
    }, [user]);

    // Get the current cart items directly from the state
    const { state: orderState } = useOrder();

    const handleContinueDraft = async (draftOrder) => {
        try {
            // Check if there are items in the current cart
            const currentCart = orderState.cartItems;

            // If there are items in the cart, ask for confirmation
            if (Array.isArray(currentCart) && currentCart.length > 0) {
                const result = await Swal.fire({
                    title: 'Conferma',
                    text: 'Continuando con questa bozza, l\'ordine attuale verrà svuotato. Vuoi procedere?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Sì, procedi',
                    cancelButtonText: 'Annulla'
                });

                if (!result.isConfirmed) {
                    return;
                }

                // Empty the cart before loading the draft
                dispatch({ type: 'RESET_CART' });
            }

            // Parse the order data and cart items
            const orderDetails = JSON.parse(draftOrder.order_data);
            const cartItems = draftOrder.cart_items.map(item => JSON.parse(item.product_data));

            // Fetch complete customer data from database
            let customerData = { CustSupp: draftOrder.customer_id };
            try {
                const clientResponse = await api.getClientById(draftOrder.customer_id);
                if (clientResponse.status === 'success' && clientResponse.client) {
                    customerData = clientResponse.client;
                }
            } catch (clientErr) {
                console.warn('Could not fetch client details, using minimal data:', clientErr);
            }

            // Load the draft order into the context
            dispatch({
                type: 'LOAD_DRAFT_ORDER',
                payload: {
                    id: draftOrder.id,
                    orderDetails,
                    cartItems,
                    customer: customerData,
                    // Bozza nata da "Duplica e Modifica": al completamento l'ordine originale va annullato
                    replacesOrderNumber: orderDetails && orderDetails.originalOrderNumber ? orderDetails.originalOrderNumber : null
                }
            });

            // Navigate to the order summary
            navigate('/order-summary');
        } catch (err) {
            console.error('Error loading draft order:', err);
            setError('Errore nel caricamento della bozza');
        }
    };

    const handleDeleteDraft = async (id) => {
        const result = await Swal.fire({
            title: 'Conferma',
            text: 'Sei sicuro di voler eliminare questa bozza?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sì, elimina',
            cancelButtonText: 'Annulla'
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const response = await api.deleteDraftOrder(id);
            if (response.status === 'success') {
                // Remove the deleted draft from the list
                setDraftOrders(draftOrders.filter(draft => draft.id !== id));
            } else {
                setError('Errore nell\'eliminazione della bozza');
            }
        } catch (err) {
            console.error('Error deleting draft order:', err);
            setError('Errore nell\'eliminazione della bozza');
        }
    };

    if (loading) {
        return <div className="text-center py-8">Caricamento bozze...</div>;
    }

    if (error) {
        return <div className="text-center py-8 text-red-600">{error}</div>;
    }

    if (draftOrders.length === 0) {
        return <div className="text-center py-8">Nessuna bozza disponibile</div>;
    }

    return (
        <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Ordini in sospeso</h2>
            <div className="space-y-4">
                {draftOrders.map((draft) => {
                    // Parse the order data to display details
                    let orderData = {};
                    try {
                        orderData = JSON.parse(draft.order_data);
                    } catch (e) {
                        console.error('Error parsing order data:', e);
                    }

                    return (
                        <div key={draft.id} className="bg-white rounded-lg shadow p-4">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="font-semibold">
                                        Sospeso #{draft.id}
                                    </h3>
                                    <p className="text-sm text-gray-500">
                                        Creata il: {new Date(draft.created_at).toLocaleString()}
                                    </p>
                                    {orderData.productCode && (
                                        <p className="mt-2 font-mono">Ultimo Codice: {orderData.productCode}</p>
                                    )}
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => handleContinueDraft(draft)}
                                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                    >
                                        Continua
                                    </button>
                                    <button
                                        onClick={() => handleDeleteDraft(draft.id)}
                                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                    >
                                        Elimina
                                    </button>
                                </div>
                            </div>

                            {/* Display cart items with wizard steps */}
                            <div className="mt-4 border-t pt-3">
                                <h4 className="font-medium mb-2">
                                    Prodotti nell'ordine: {draft.cart_items?.length || 0}
                                </h4>

                                {draft.cart_items && draft.cart_items.length > 0 && (
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                        {draft.cart_items.map((cartItem, idx) => {
                                            let itemData = {};
                                            try {
                                                itemData = JSON.parse(cartItem.product_data);
                                                // Log the structure to help debug
                                                console.log('Parsed itemData structure:', itemData);
                                            } catch (e) {
                                                console.error('Error parsing cart item data:', e);
                                            }

                                            // Extract profile info - handle different possible structures
                                            const profileInfo = itemData.wizardSteps?.profile
                                                ? (itemData.wizardSteps.profile.profileType || itemData.wizardSteps.profile.ninfaNumber)
                                                : (itemData.profileType || itemData.ninfaNumber || 'N/A');

                                            // Extract glass info - handle different possible structures
                                            const glassInfo = itemData.wizardSteps?.glass?.glassSize
                                                ? itemData.wizardSteps.glass.glassSize
                                                : (itemData.glassSize || 'N/A');

                                            // Extract length - could be at top level or in wizardSteps.length
                                            const lengthValue = itemData.length ||
                                                (itemData.wizardSteps?.length?.length) ||
                                                'N/A';

                                            // Extract quantity - could be at top level
                                            const quantityValue = itemData.quantity || 'N/A';

                                            return (
                                                <div key={idx} className="p-2 bg-gray-50 rounded border border-gray-200">
                                                    <p className="font-mono text-sm">{itemData.productCode || 'Prodotto senza codice'}</p>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        <span className="text-xs px-2 py-1 bg-red-100 rounded-full">
                                                            Profilo: {profileInfo}
                                                        </span>
                                                        <span className="text-xs px-2 py-1 bg-green-100 rounded-full">
                                                            Vetro: {glassInfo}mm
                                                        </span>
                                                        <span className="text-xs px-2 py-1 bg-indigo-100 rounded-full">
                                                            {lengthValue}mm
                                                        </span>
                                                        <span className="text-xs px-2 py-1 bg-yellow-100 rounded-full">
                                                            Qtà: {quantityValue}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DraftOrdersList;